import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, UploadCloud, MousePointer2, PenTool, MapPin, Zap, Droplet, Users, Music, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';
import { DocumentDropzone } from '../components/DocumentDropzone';

interface PlanElement {
  id: string;
  type: 'stand' | 'zone' | 'pin_elec' | 'pin_eau' | 'pin_public' | 'pin_scene';
  name?: string;
  x: number;
  y: number;
  w?: number;
  h?: number;
  price?: number;
}

export default function CreateFestivalStep4Page() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTool, setActiveTool] = useState<'pointer' | 'draw' | 'pin'>('pointer');
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  const [planElements, setPlanElements] = useState<PlanElement[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState({ x: 0, y: 0 });
  const [currentDraw, setCurrentDraw] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [customPlanUrl, setCustomPlanUrl] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!user) return;
      const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
      try {
        const docRef = doc(db, 'festivals', activeId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.planElements) setPlanElements(data.planElements);
          if (data.customPlanUrl) setCustomPlanUrl(data.customPlanUrl);
        }
      } catch (err) {
        console.error(err);
      }
    }
    loadData();
  }, [user]);

  const saveElements = async (elements: PlanElement[]) => {
    if (!user) return;
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
    try {
      await setDoc(doc(db, 'festivals', activeId), {
        planElements: elements,
        customPlanUrl
      }, { merge: true });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
    try {
      await saveElements(planElements);
      
      // Mark as published
      await setDoc(doc(db, 'festivals', activeId), {
        status: 'published'
      }, { merge: true });

      // Notifie les exposants compatibles (best-effort, non bloquant)
      try {
        const token = await user.getIdToken();
        fetch(`${import.meta.env.VITE_API_URL || ''}/api/festival/${activeId}/notify-matches`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      } catch { /* non critique */ }

      // Clear active session
      sessionStorage.removeItem('activeFestivalId');

      navigate('/home', { state: { role: 'festival' } });
    } catch (err) {
      console.error("Error finalizing festival", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetour = async () => {
    setIsSavingDraft(true);
    await saveElements(planElements);
    setIsSavingDraft(false);
    navigate(-1);
  };

  const selectedElement = planElements.find(e => e.id === selectedElementId);

  const handleMapMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activeTool === 'pointer') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (activeTool === 'draw') {
      setIsDrawing(true);
      setDrawStart({ x, y });
      setCurrentDraw({ x, y, w: 0, h: 0 });
    } else if (activeTool === 'pin') {
      const newPin: PlanElement = {
        id: Date.now().toString(),
        type: 'pin_elec',
        x: x - 15,
        y: y - 15,
        w: 30,
        h: 30
      };
      setPlanElements(prev => [...prev, newPin]);
      setActiveTool('pointer');
    }
  };

  const handleMapMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawing || activeTool !== 'draw') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setCurrentDraw({
      x: Math.min(drawStart.x, x),
      y: Math.min(drawStart.y, y),
      w: Math.abs(x - drawStart.x),
      h: Math.abs(y - drawStart.y)
    });
  };

  const handleMapMouseUp = () => {
    if (isDrawing && activeTool === 'draw') {
      if (currentDraw.w > 20 && currentDraw.h > 20) {
        const newZone: PlanElement = {
          id: Date.now().toString(),
          type: 'zone',
          name: 'Nouvelle Zone',
          price: 150,
          x: currentDraw.x,
          y: currentDraw.y,
          w: currentDraw.w,
          h: currentDraw.h
        };
        setPlanElements(prev => [...prev, newZone]);
        setSelectedElementId(newZone.id);
      }
      setActiveTool('pointer');
    }
    setIsDrawing(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const typeStr = e.dataTransfer.getData('text/plain');
    if (!typeStr) return;
    let type: PlanElement['type'];
    if (typeStr === 'elec') type = 'pin_elec';
    else if (typeStr === 'eau') type = 'pin_eau';
    else if (typeStr === 'public') type = 'pin_public';
    else if (typeStr === 'scene') type = 'pin_scene';
    else return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - 15;
    const y = e.clientY - rect.top - 15;

    const newPin: PlanElement = {
      id: Date.now().toString(),
      type,
      x,
      y,
      w: 30,
      h: 30
    };
    setPlanElements(prev => [...prev, newPin]);
  };

  const updateSelectedPrice = (price: number) => {
    if (!selectedElementId) return;
    setPlanElements(prev => prev.map(el => el.id === selectedElementId ? { ...el, price } : el));
  };
  
  const updateSelectedName = (name: string) => {
    if (!selectedElementId) return;
    setPlanElements(prev => prev.map(el => el.id === selectedElementId ? { ...el, name } : el));
  };

  const deleteSelected = () => {
    if (!selectedElementId) return;
    setPlanElements(prev => prev.filter(el => el.id !== selectedElementId));
    setSelectedElementId(null);
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans flex flex-col overflow-hidden h-screen">
      {/* Header */}
      <FestivalCreationHeader onBack={handleRetour} />

      {/* Progress Bar Container */}
      <div className="bg-[#121212] border-b border-white/5 px-4 md:px-6 py-3 md:py-4 flex-none">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between text-[10px] md:text-xs font-black text-gray-500 mb-2 md:mb-4 uppercase tracking-[0.2em]">
            <span>Planification • Étape 5/5</span>
            <span className="text-[#ff5a00] hidden sm:inline">Plan de masse</span>
          </div>
          <div className="grid grid-cols-5 gap-1.5 md:gap-2">
            {[1, 2, 3, 4, 5].map((step) => (
              <div 
                key={step}
                className={`h-1 md:h-1.5 rounded-full transition-all duration-500 ${
                  step <= 5 
                    ? 'bg-[#FF5E00] shadow-[0_0_10px_rgba(255,94,0,0.4)]' 
                    : 'bg-white/5'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        
        {/* Overlay Bientôt */}
        <div className="absolute inset-0 z-50 backdrop-blur-xl bg-black/70 overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center p-4 md:p-8">
            <div className="max-w-2xl w-full bg-[#1A1A1A] border border-white/10 rounded-3xl p-6 md:p-12 text-center shadow-2xl relative overflow-hidden">
               
               {/* Decorative background glow */}
               <div className="absolute -top-32 -left-32 w-64 h-64 bg-[#FF5C00]/20 rounded-full blur-[80px] pointer-events-none" />
               <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] pointer-events-none" />

               <div className="relative z-10">
                 <div className="w-16 h-16 md:w-20 md:h-20 bg-[#FF5C00]/10 border border-[#FF5C00]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                   <MapPin className="w-8 h-8 md:w-10 md:h-10 text-[#FF5C00]" />
                 </div>
                 
                 <h2 className="text-xl md:text-3xl lg:text-4xl font-black text-white uppercase tracking-tight mb-4">
                   Bientôt : Planification Connectée
                 </h2>
                 <p className="text-gray-400 font-medium mb-8 text-xs md:text-base leading-relaxed">
                   Notre outil de placement interactif sur plan est en cours de développement. En attendant le déploiement de cette fonctionnalité native, vous pouvez partager votre plan existant afin que les exposants puissent s'y repérer facilement !
                 </p>

                 <div className="text-left bg-black/40 p-4 md:p-6 rounded-2xl border border-white/5 space-y-4">
                   <DocumentDropzone 
                     label="Plan de votre village (Optionnel)"
                     storagePath={`festivals/${sessionStorage.getItem('activeFestivalId') || user?.uid}/custom_plan`}
                     accept="application/pdf,image/*"
                     onUploadComplete={(url) => setCustomPlanUrl(url)}
                     existingUrl={customPlanUrl}
                     description="Importez le plan final, il sera visible sur la vitrine du festival."
                   />
                 </div>
               </div>
            </div>
          </div>
        </div>

        {/* Left Sidebar - Toolbox */}
        <aside className="w-20 md:w-64 bg-[#121212] border-r border-white/5 flex flex-col z-20 py-6">
          <div className="px-4 mb-4 hidden md:block">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Outils</h3>
          </div>
          <div className="flex flex-col space-y-2 px-2 md:px-4 mb-8">
             <button 
                onClick={() => setActiveTool('pointer')}
                className={`p-3 rounded-xl flex items-center justify-center md:justify-start transition-all ${activeTool === 'pointer' ? 'bg-[#FF5C00]/10 text-[#FF5C00]' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
             >
               <MousePointer2 className="w-5 h-5 md:mr-3" />
               <span className="hidden md:inline font-bold text-sm">Sélectionner</span>
             </button>
             <button 
                onClick={() => setActiveTool('draw')}
                className={`p-3 rounded-xl flex items-center justify-center md:justify-start transition-all ${activeTool === 'draw' ? 'bg-[#FF5C00]/10 text-[#FF5C00]' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
             >
               <PenTool className="w-5 h-5 md:mr-3" />
               <span className="hidden md:inline font-bold text-sm">Tracer une Zone</span>
             </button>
             <button 
                onClick={() => setActiveTool('pin')}
                className={`p-3 rounded-xl flex items-center justify-center md:justify-start transition-all ${activeTool === 'pin' ? 'bg-[#FF5C00]/10 text-[#FF5C00]' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
             >
               <MapPin className="w-5 h-5 md:mr-3" />
               <span className="hidden md:inline font-bold text-sm">Placer un Pin (Élec)</span>
             </button>
          </div>

          <div className="px-4 mb-4 hidden md:block">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Ressources (Drag & Drop)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-2 md:px-4">
             <button 
               draggable
               onDragStart={(e) => e.dataTransfer.setData('text/plain', 'elec')}
               className="p-3 bg-[#1A1A1A] hover:bg-[#1a1a1a] border border-white/5 rounded-xl flex flex-col items-center justify-center transition-all group cursor-grab active:cursor-grabbing"
             >
               <Zap className="w-5 h-5 text-yellow-500 mb-1" />
               <span className="hidden md:block text-[10px] font-bold text-gray-400 group-hover:text-white">Élec</span>
             </button>
             <button 
               draggable
               onDragStart={(e) => e.dataTransfer.setData('text/plain', 'eau')}
               className="p-3 bg-[#1A1A1A] hover:bg-[#1a1a1a] border border-white/5 rounded-xl flex flex-col items-center justify-center transition-all group cursor-grab active:cursor-grabbing"
             >
               <Droplet className="w-5 h-5 text-blue-500 mb-1" />
               <span className="hidden md:block text-[10px] font-bold text-gray-400 group-hover:text-white">Eau</span>
             </button>
             <button 
               draggable
               onDragStart={(e) => e.dataTransfer.setData('text/plain', 'public')}
               className="p-3 bg-[#1A1A1A] hover:bg-[#1a1a1a] border border-white/5 rounded-xl flex flex-col items-center justify-center transition-all group cursor-grab active:cursor-grabbing"
             >
               <Users className="w-5 h-5 text-green-500 mb-1" />
               <span className="hidden md:block text-[10px] font-bold text-gray-400 group-hover:text-white">Public</span>
             </button>
             <button 
               draggable
               onDragStart={(e) => e.dataTransfer.setData('text/plain', 'scene')}
               className="p-3 bg-[#1A1A1A] hover:bg-[#1a1a1a] border border-white/5 rounded-xl flex flex-col items-center justify-center transition-all group cursor-grab active:cursor-grabbing"
             >
               <Music className="w-5 h-5 text-purple-500 mb-1" />
               <span className="hidden md:block text-[10px] font-bold text-gray-400 group-hover:text-white">Scène</span>
             </button>
          </div>
        </aside>

        {/* Center - Dark Canvas Grid */}
        <div 
          className="flex-1 relative bg-black overflow-hidden flex items-center justify-center" 
          style={{ backgroundImage: 'radial-gradient(#4169E1 1px, transparent 1px)', backgroundSize: '40px 40px', backgroundPosition: 'center center' }}
        >
          {/* Faded Background Map Image */}
          <div className="absolute inset-0 z-0 flex items-center justify-center opacity-10 pointer-events-none">
             <div className="w-[800px] h-[600px] border-2 border-[#4169E1]/30 rounded-3xl relative">
                <img 
                  src="https://images.unsplash.com/photo-1540039155733-d7696d4ebc59?auto=format&fit=crop&q=80" 
                  alt="Map Base" 
                  className="w-full h-full object-cover rounded-3xl mix-blend-luminosity grayscale"
                />
             </div>
          </div>

          <div
            className={`w-[800px] h-[600px] border-2 border-white/10 rounded-2xl relative bg-black/50 overflow-hidden z-10 ${activeTool === 'draw' || activeTool === 'pin' ? 'cursor-crosshair' : 'cursor-default'}`}
            onMouseDown={handleMapMouseDown}
            onMouseMove={handleMapMouseMove}
            onMouseUp={handleMapMouseUp}
            onMouseLeave={handleMapMouseUp}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
            }}
            onDrop={handleDrop}
          >
            {planElements.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 pointer-events-none">
                  <MapPin className="w-12 h-12 mb-4 opacity-50" />
                  <p>Aucun élément placé. Utilisez les outils pour dessiner des zones ou placer des points.</p>
              </div>
            )}

            {planElements.map(element => {
              const isSelected = selectedElementId === element.id;
              if (element.type === 'stand' || element.type === 'zone') {
                return (
                  <div 
                    key={element.id}
                    onMouseDown={(e) => {
                      if (activeTool === 'pointer') {
                        e.stopPropagation();
                        setSelectedElementId(element.id);
                      }
                    }}
                    className={`absolute flex items-center justify-center p-1 text-center shadow-lg cursor-pointer
                        ${element.type === 'stand' ? 'bg-[#FF5C00]/80 border-[#FF5C00]' : 'bg-blue-500/30 border-blue-500'}
                        ${isSelected ? 'border-4 ring-2 ring-white/50' : 'border-2'}`}
                    style={{ 
                      left: element.x, 
                      top: element.y, 
                      width: element.w, 
                      height: element.h,
                      transition: isDrawing ? 'none' : 'all 0.3s ease-out',
                      borderRadius: element.type === 'zone' ? '12px' : '4px'
                    }}
                  >
                    <span className="text-[10px] font-bold text-white leading-tight break-words overflow-hidden w-full h-full flex flex-col items-center justify-center">
                      {element.name}
                      {element.type === 'zone' && element.price !== undefined && (
                        <span className="text-[8px] bg-black/50 px-1 rounded mt-1">{element.price}€</span>
                      )}
                    </span>
                  </div>
                );
              } else {
                return (
                  <div
                    key={element.id}
                    onMouseDown={(e) => {
                      if (activeTool === 'pointer') {
                        e.stopPropagation();
                        setSelectedElementId(element.id);
                      }
                    }}
                    className={`absolute flex items-center justify-center rounded-full shadow-lg flex-col cursor-pointer
                        ${element.type === 'pin_elec' ? 'bg-yellow-500 border-2 border-black h-8 w-8' : 
                          element.type === 'pin_eau' ? 'bg-blue-500 border-2 border-black h-8 w-8' : 
                          element.type === 'pin_public' ? 'bg-green-500 border-2 border-black h-8 w-8' : 'bg-purple-500 border-2 border-black h-10 w-10'}
                        ${isSelected ? 'ring-4 ring-white shadow-[0_0_15px_rgba(255,255,255,0.5)]' : ''}`}
                    style={{
                      left: element.x,
                      top: element.y,
                    }}
                  >
                    {element.type === 'pin_elec' && <Zap className="w-4 h-4 text-black" />}
                    {element.type === 'pin_eau' && <Droplet className="w-4 h-4 text-white" />}
                    {element.type === 'pin_public' && <Users className="w-4 h-4 text-white" />}
                    {element.type === 'pin_scene' && <Music className="w-5 h-5 text-white" />}
                  </div>
                );
              }
            })}

            {isDrawing && activeTool === 'draw' && (
              <div 
                className="absolute border-2 border-blue-500/50 bg-blue-500/20"
                style={{
                  left: currentDraw.x,
                  top: currentDraw.y,
                  width: currentDraw.w,
                  height: currentDraw.h,
                  borderRadius: '12px'
                }}
              />
            )}
          </div>
        </div>

        {/* Right Sidebar - Details Panel */}
        <aside className="w-80 bg-[#121212] border-l border-white/5 flex flex-col z-20 overflow-y-auto">
           {selectedElement ? (
             <div className="p-6">
                <div className="flex items-center justify-between space-x-3 mb-6 border-b border-white/10 pb-4">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${selectedElement.type === 'zone' ? 'bg-[#4169E1]' : 'bg-[#FF5C00]'}`} />
                    <h2 className="text-lg font-bold text-white uppercase tracking-tight">
                      Édition
                    </h2>
                  </div>
                  <button onClick={deleteSelected} className="p-2 hover:bg-white/5 rounded-lg text-red-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 text-uppercase tracking-wider mb-2">Type d'élément</label>
                    <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-3 font-medium text-sm text-white">
                       {selectedElement.type === 'zone' && 'Zone Réservable'}
                       {selectedElement.type === 'stand' && 'Stand Placé'}
                       {selectedElement.type === 'pin_elec' && 'Point d\'Électricité'}
                       {selectedElement.type === 'pin_eau' && 'Point d\'Eau'}
                       {selectedElement.type === 'pin_public' && 'Accès Public'}
                       {selectedElement.type === 'pin_scene' && 'Scène Principale'}
                    </div>
                  </div>

                  {(selectedElement.type === 'zone' || selectedElement.type === 'stand') && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 text-uppercase tracking-wider mb-2">Nom de {selectedElement.type === 'zone' ? 'la zone' : 'l\'élément'}</label>
                      <input 
                        type="text" 
                        value={selectedElement.name || ''} 
                        onChange={(e) => updateSelectedName(e.target.value)}
                        className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl p-3 text-white font-bold focus:outline-none focus:border-[#4169E1] transition-colors"
                      />
                    </div>
                  )}

                  {selectedElement.type === 'zone' && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 text-uppercase tracking-wider mb-2">Tarif de base estimé (€ / jour)</label>
                      <input 
                        type="number" 
                        value={selectedElement.price || 0} 
                        onChange={(e) => updateSelectedPrice(parseInt(e.target.value) || 0)}
                        className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl p-3 text-[#4169E1] font-black text-xl focus:outline-none focus:border-[#4169E1] transition-colors"
                      />
                    </div>
                  )}

                  <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-4">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Position</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-black py-2 px-3 rounded-lg border border-white/5">X: {Math.round(selectedElement.x)}</div>
                      <div className="bg-black py-2 px-3 rounded-lg border border-white/5">Y: {Math.round(selectedElement.y)}</div>
                    </div>
                  </div>

                </div>
             </div>
           ) : (
             <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
               <MousePointer2 className="w-12 h-12 text-gray-600 mb-4" />
               <p className="text-gray-400 font-medium">Sélectionnez une zone ou un point sur le plan pour afficher les détails et l'éditer.</p>
             </div>
           )}
        </aside>
      </div>

      {/* Bottom Navigation */}
      <div className="flex-none bg-[#121212] border-t border-white/5 px-4 md:px-6 py-4 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <button onClick={handleRetour} disabled={isSavingDraft} className="w-full md:w-auto px-8 py-4 rounded-xl md:rounded-2xl border border-white/10 text-white font-bold hover:bg-white/10 transition-all text-sm disabled:opacity-50">Précédent</button>
          <p className="text-[10px] md:text-xs text-gray-500 font-medium hidden md:block">Votre festival sera visible par les exposants après publication.</p>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full md:w-auto bg-white text-black font-black py-4 px-10 md:px-12 rounded-xl md:rounded-2xl hover:bg-[#ff5e00] hover:text-white transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer`}
          >
            {isSaving ? 'PUBLICATION...' : "PUBLIER LE FESTIVAL"}
            {!isSaving && <Zap className="w-4 h-4 md:w-5 md:h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
